// src/services/api/uploadClient.js
import { apiFetch } from '../../auth/apiFetch.js';
import { API_V1, getSessionHeaders } from './config.js';
import { reportIfTenantPurged } from '../../auth/tenantPurged.js';

/**
 * Fetch current bundle info (file list).
 * Returns { files: [...] } or { files: [] } if 404.
 */
export async function getBundle() {
  const res = await apiFetch('/api/v1/uploads/bundle');
  if (res.status === 404) return { files: [] };
  if (!res.ok) throw new Error(`Bundle fetch failed: ${res.status}`);
  const data = await res.json();
  // Normalize backend fields to frontend shape: { filename, chunks, size }
  const files = (data.files || []).map(f => ({
    filename: f.name || f.filename,
    chunks: f.chunk_count ?? f.chunks,
    size: f.size,
  }));
  return { files };
}

/**
 * Upload a file to the bundle (multipart/form-data).
 * Uses raw fetch because apiFetch sets Content-Type: application/json,
 * which breaks FormData (browser must set multipart boundary).
 */
export async function uploadFileToBundle(file) {
  const token = localStorage.getItem('auth_token');
  const tenantId = localStorage.getItem('auth_tenant_id');
  const formData = new FormData();
  formData.append('file', file);

  const res = await fetch(`${API_V1}/uploads/bundle`, {
    method: 'POST',
    headers: {
      ...getSessionHeaders(),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(tenantId ? { 'X-Tenant-Id': tenantId } : {}),
      // No Content-Type — browser sets multipart boundary automatically
    },
    body: formData,
  });
  if (!res.ok) {
    await reportIfTenantPurged(res, tenantId);
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `Upload failed: ${res.status}`);
  }
  return res.json();
}

/**
 * Delete a single file from the bundle.
 */
export async function deleteBundleFile(filename) {
  const res = await apiFetch(`/api/v1/uploads/bundle/${encodeURIComponent(filename)}`, {
    method: 'DELETE',
  });
  if (!res.ok) throw new Error(`Delete failed: ${res.status}`);
  return res.json();
}

/**
 * Delete entire bundle.
 */
export async function deleteBundle() {
  const res = await apiFetch('/api/v1/uploads/bundle', {
    method: 'DELETE',
  });
  if (!res.ok) throw new Error(`Bundle delete failed: ${res.status}`);
  return res.json();
}
